import {type GatewayConfiguration} from '../../models/GatewayConfiguration';
import {type SquareGateway, type ProcessedTokenResult} from '../../models/square';
import {store} from '../../store';
import {getLocaleText} from '../../util/translation';
import {render} from '../../reducers/rootReducer';
import {handleTokenResult} from './square';
import {PeachPayCustomer} from '../../reducers/peachPayCustomerReducer';
import {type PlaidEventName, type ACH, type Payments} from '@square/web-payments-sdk-types';
import achBankImageURL from '../../../img/marks/bank.svg';
import {Feature} from '../../reducers/environmentReducer';
import {FeatureFlag} from '../../../../@type/features';
import {captureSentryException} from '../../../../@shared/ts/sentry';

function initSquareACHDebitMethod(): SquareGateway {
	let ach: ACH | null = null;
	let activated = false;

	const config: GatewayConfiguration = {
		name: Feature.metadata<string>(FeatureFlag.SQUARE_ACH, 'title') ?? getLocaleText('US Bank Account'),
		gatewayId: 'peachpay_square_ach',
		description: getDescription(),
		assets: {
			badge: {src: achBankImageURL},
			title: {src: achBankImageURL},
		},
	};

	return {
		config,
		async activate(payments: Payments) {
			if (activated) {
				return;
			}

			activated = true;

			try {
				ach = await payments.ach({
					redirectURI: window.location.href,
					transactionId: '0',
				});

				ach.addEventListener('ontokenization' as PlaidEventName.ON_TOKENIZATION, _event => {
					// Required for plaid authentication to succeed. Event matches TokenResult from tokenize().
				});

				config.initialized = true;
				store.dispatch(render());
			} catch (error: unknown) {
				config.initialized = false;
				store.dispatch(render());

				console.error(error);
				captureSentryException(error as Error);
			}
		},
		async tokenize(): Promise<ProcessedTokenResult> {
			try {
				if (!ach) {
					return [null, null, {
						paymentStatus: 'creationfailed',
						note: 'Failed to find ACH Bank instance while attempting to tokenize the payment.',
					}];
				}

				const result = await ach.tokenize({
					accountHolderName: PeachPayCustomer.billing.fullName(),
				});
				return handleTokenResult(result);
			} catch (error: unknown) {
				const update: ITransactionUpdate = {
					paymentStatus: 'creationfailed',
					note: 'An unknown exception occured while tokenizing the payment.',
				};

				if (error instanceof Error) {
					update.note = error.message;
				}

				return [null, null, update];
			}
		},
	};
}

function getDescription(): string {
	return /* html */ `
	<p style="margin: 0;">
		${getLocaleText('After selecting <b>Pay</b> a prompt will appear to select a bank account.')}
	</p>
	<hr>
	<p class="muted" style="margin: 0;">
		${getLocaleText('Note: Payments may take up to 5 days to complete.')}
	</p>
	`;
}

export {initSquareACHDebitMethod};

